import { cloneDeep } from 'lodash';

import { getTrainingLocalGameSnapshotKey, STORAGE_KEY_ALIASES } from '~/app/storageKeys';
import { legacyLocalGameSnapshotSchema, localGameSnapshotSchema } from '~/app/storageSchemas';
import { getCurrentStorageUserScopeId } from '~/app/storageScope';
import { safeParseStoredJson } from '~/app/storageValidation';
import { BOT_PACING_LOCAL_TRAINING } from '~/server/src/botPacing';
import { GameState, GameStatus, GameType } from '~/server/src/dtos';
import { Controller, View } from '~/server/src/interfaces';
import Computer from '~/server/src/tmcomputer';
import { ControllerLocalWithEventLog } from '~/server/src/tmcontrollerlocalwitheventlog';
import Turfmaster, { Board, Game, StateMachineValue } from '~/server/src/tmmodel';

import { gameLogger } from './logger';

export const localGameSnapshotKey = STORAGE_KEY_ALIASES.trainingLocalGameSnapshot[0];

const playerId = 0;

const resolveLocalSnapshotKey = (userScopeId?: string): string =>
  getTrainingLocalGameSnapshotKey(userScopeId ?? getCurrentStorageUserScopeId());

/** Persist a training-mode snapshot for local restore/reload. */
export const saveLocalGameSnapshot = (snapshot: GameState, userScopeId?: string): void => {
  const canonicalKey = resolveLocalSnapshotKey(userScopeId);
  localStorage.setItem(canonicalKey, JSON.stringify(snapshot));
};

export const clearLocalGameSnapshot = (userScopeId?: string): void => {
  const canonicalKey = resolveLocalSnapshotKey(userScopeId);
  localStorage.removeItem(canonicalKey);
  // Cleanup old unscoped snapshot keys as part of migration rollout.
  // TODO: [storage-migration-cleanup] Remove legacy key cleanup once migration is complete.
  for (const legacyKey of STORAGE_KEY_ALIASES.trainingLocalGameSnapshot) {
    localStorage.removeItem(legacyKey);
  }
};

export const loadLocalGameSnapshot = (userScopeId?: string): GameState | null => {
  const canonicalKey = resolveLocalSnapshotKey(userScopeId);
  const canonicalRaw = localStorage.getItem(canonicalKey);
  const canonicalResult = safeParseStoredJson(canonicalRaw, localGameSnapshotSchema);
  if (canonicalResult.ok) return canonicalResult.data as GameState;

  // Backward-compat: Older builds stored a different shape under the canonical key
  // (e.g. `{ game, gameId, gameType, lastRemovedCards }`). Migrate it if possible.
  if (canonicalRaw != null) {
    const legacyCanonicalResult = safeParseStoredJson(canonicalRaw, legacyLocalGameSnapshotSchema);
    if (legacyCanonicalResult.ok) {
      const legacy = legacyCanonicalResult.data as {
        gameId: string;
        gameType: string | number;
        game: Record<string, unknown>;
        lastRemovedCards: unknown[];
      };
      const normalized: GameState = {
        gameId: legacy.gameId,
        timeCreated: new Date().toISOString(),
        gameType: legacy.gameType as GameType,
        boardId:
          typeof legacy.game?.boardid === 'number' && Number.isFinite(legacy.game.boardid)
            ? (legacy.game.boardid as number)
            : 0,
        status: GameStatus.Running,
        eventLog: [],
        lastRemovedCards: (legacy.lastRemovedCards ?? [])
          .map((entry) => (typeof entry === 'number' ? entry : Number(entry)))
          .filter((value) => Number.isFinite(value)),
        state: legacy.game,
        statePrev: undefined,
        statePrevPrev: undefined,
        visibility: 'private',
        allowSpectators: false,
        chatVisibility: 'playersonly',
      };
      localStorage.setItem(canonicalKey, JSON.stringify(normalized));
      return normalized;
    }
  }

  if (canonicalResult.reason !== 'missing') {
    gameLogger.warn({ key: canonicalKey }, 'invalid canonical game snapshot in storage; dropping');
    localStorage.removeItem(canonicalKey);
  }

  // Legacy fallback: read old key(s) and rewrite to canonical account-scoped key.
  // TODO: [storage-migration-cleanup] Remove legacy read path after migration window.
  for (const legacyKey of STORAGE_KEY_ALIASES.trainingLocalGameSnapshot) {
    const legacyResult = safeParseStoredJson(
      localStorage.getItem(legacyKey),
      localGameSnapshotSchema,
    );
    if (legacyResult.ok) {
      localStorage.setItem(canonicalKey, JSON.stringify(legacyResult.data));
      return legacyResult.data as GameState;
    }
    if (legacyResult.reason !== 'missing') {
      gameLogger.warn({ key: legacyKey }, 'invalid legacy game snapshot in storage; dropping');
      localStorage.removeItem(legacyKey);
    }
  }

  // fallback solution, return null instead of throwing an exception
  return null;
};

class ClientControllerLocal extends ControllerLocalWithEventLog implements Controller {
  public constructor(mode: GameType = GameType.Training) {
    super(mode);
  }

  /**
   * Receive an event from client and handle it. Restore / Rejoin events are handled here,
   * the rest is handled by the localController.
   *
   * @param state new game state
   * @param params params of new game state
   */
  public receiveMsg = async (state: StateMachineValue, params: unknown): Promise<false | void> => {
    switch (state) {
      case Turfmaster.stateMachine.RESTOREGAME: {
        const restoreParams = params as { view: View; userId?: string };
        const foundSnapshot = loadLocalGameSnapshot(restoreParams.userId);

        // send rehydrated game to client to indicate game would be restorable
        // send null if not, client knows how to handle this
        if (foundSnapshot) gameLogger.debug({ snapshot: foundSnapshot }, 'SNAPSHOT FOUND');
        else gameLogger.debug('SNAPSHOT NOT FOUND');

        const view = restoreParams.view;
        if (view) {
          return await view.receiveMsg(Turfmaster.stateMachine.RESTOREGAME, {
            game: cloneDeep(foundSnapshot),
          });
        }
        break;
      }
      case Turfmaster.stateMachine.REJOINGAME: {
        const rejoinParams = params as { view: View; userId?: string };
        const snapshot = loadLocalGameSnapshot(rejoinParams.userId);

        if (snapshot) {
          this.gameid = snapshot.gameId;
          this.board = new Board(snapshot.boardId);
          this.gameType = snapshot.gameType;
          this.game = snapshot.state;
          this.lastRemovedCards = snapshot.lastRemovedCards;
          // Restore event log from snapshot
          this.setEventLog(snapshot.eventLog || []);

          const playerView = rejoinParams.view;
          this.playerViews = [playerView];

          for (let i = 1; i < this.game.players.length; i++) {
            // TODO: fix type of computer state
            const computerState = this.game.players[i] as unknown as View & { strength: number };
            const computer = new Computer(
              i,
              computerState.strength,
              this,
              BOT_PACING_LOCAL_TRAINING,
            );
            this.playerViews[i] = computer;
          }

          // confirm rejoin and send playerid to player which is always 0 in training mode
          // because everything is local and this is hardcoded in sagaSetupTraining
          // all other players are bots
          await playerView.receiveMsg(Turfmaster.stateMachine.REJOINGAME, { playerid: playerId });

          // Resend SELECTCARDDICE/current state as needed to trigger moves/dice/bot actions
          // Reuses the same logic as returning from history mode (rejoinGame method)
          await this.rejoinGame();

          gameLogger.info(
            {
              snapshot: {
                gameId: snapshot.gameId,
                status: snapshot.status,
                state: snapshot.state.currentState,
                gameType: snapshot.gameType,
                timeCreated: snapshot.timeCreated,
              },
            },
            'snapshot restored',
          );
        } else {
          gameLogger.warn('snapshot restore error: not found');
        }

        break;
      }
      default:
        return await super.receiveMsg(state, cloneDeep(params));
    }
  };

  /**
   * Take message that will be send to the client by the localcontroller,
   * persist the current game state and then let it continue to the client.
   *
   * TODO: delete persisted game state, if game is finished
   *
   * @param state new game state
   * @param params params of new game state
   */
  public sendMsg = async (state: StateMachineValue, params: unknown): Promise<false | void> => {
    const clonedParams = cloneDeep(params);
    const oldLocalGameSnapshot = loadLocalGameSnapshot();
    const shouldPersistSnapshot =
      state !== Turfmaster.stateMachine.ENDROUND &&
      state !== Turfmaster.stateMachine.ADDBONUSCARDSOPP &&
      state !== Turfmaster.stateMachine.SELECTMOVEOPP &&
      state !== Turfmaster.stateMachine.MERGECARDSOPP &&
      state !== Turfmaster.stateMachine.SELECTDICESOPP &&
      state !== Turfmaster.stateMachine.NEWMESSAGE &&
      state !== Turfmaster.stateMachine.SENDMESSAGE &&
      state !== Turfmaster.stateMachine.LEAVEGAME &&
      state !== Turfmaster.stateMachine.LEAVEGAMEOPP;

    // Skip transient states that are NOT in stateMatrix and can't be meaningfully restored
    // Match ControllerLocalProxy behavior to prevent saving snapshots with invalid states
    if (shouldPersistSnapshot) {
      this.updateState(state);
      const gameState = super.getGameSnapshot();
      if (!gameState) {
        gameLogger.warn('SNAPSHOT ERROR: controller did not return a snapshot');
        return await super.sendMsg(state, clonedParams);
      }

      const localGameSnapshot: GameState = {
        gameId: this.gameid,
        timeCreated: this.timecreated,
        gameType: this.gameType,
        boardId: gameState.boardid,
        status: GameStatus.Running,
        eventLog: this.getEventLog(),
        lastRemovedCards: this.lastRemovedCards,
        state: gameState,
        statePrev: oldLocalGameSnapshot?.state,
        statePrevPrev: oldLocalGameSnapshot?.statePrev,
        visibility: 'private',
        allowSpectators: false,
        chatVisibility: 'playersonly',
      };
      const snapshotKey = resolveLocalSnapshotKey();
      localStorage.setItem(snapshotKey, JSON.stringify(localGameSnapshot));
      gameLogger.debug({ snapshot: localGameSnapshot }, 'SNAPSHOT SAVED');
    } else {
      // no-op: transient states are intentionally not persisted
    }

    return await super.sendMsg(state, clonedParams);
  };

  public getGame = (): Game => {
    return cloneDeep(super.getGame());
  };

  public getBoard = (): Board => {
    return cloneDeep(super.getBoard());
  };

  /**
   * Simulate REJOINGAME resending logic when returning to live mode from history.
   * This reuses the same resending logic as browser refresh/reconnect, ensuring consistent behavior.
   * Unlike REJOINGAME, this does NOT load from snapshot (we're already in a game).
   * Resends SELECTCARDDICE/current state as needed to trigger moves/dice/bot actions.
   */
  public rejoinGame = async (): Promise<void> => {
    if (!this.game) return;

    const playerView = this.playerViews[0];
    if (!playerView) return;

    const curState = this.game.currentState;
    const isPreStartState =
      curState === Turfmaster.stateMachine.NEWGAME || curState === Turfmaster.stateMachine.JOINGAME;
    const isStartGameState = curState === Turfmaster.stateMachine.STARTGAME;
    const isStartRoundState = curState === Turfmaster.stateMachine.STARTROUND;
    const isStartTurnState = curState === Turfmaster.stateMachine.STARTTURN;
    const isSelectDicesState = curState === Turfmaster.stateMachine.SELECTDICES;
    const isPickDiceState =
      curState === Turfmaster.stateMachine.SELECTMOVE ||
      curState === Turfmaster.stateMachine.SELECTCARDDICE;
    const isAfterOppRoll = curState === Turfmaster.stateMachine.SELECTDICESOPP;

    // Restored pre-start snapshots can otherwise stay forever in NEWGAME/JOINGAME.
    // Mirror newGameEvent/joinGameEvent behavior and actively start when lobby is complete.
    if (isPreStartState && !this.isHorseLeft()) {
      await this.startGame();
      return;
    }

    // Persist can happen mid-transition after START* was sent but before follow-up state.
    // Continue exactly where the controller would have gone next.
    if (isStartGameState) {
      await this.startRound();
      return;
    }
    if (isStartRoundState) {
      if (this.game.round > 0) {
        this.lastRemovedCards = this.updateBonusCards(this.getHorseId());
        await this.sendMsg(Turfmaster.stateMachine.MERGECARDS, {
          removedCards: this.lastRemovedCards,
        });
      } else {
        await this.startTurn();
      }
      return;
    }
    if (isStartTurnState) {
      if (this.game.turntype === Turfmaster.turntypes.DICES) {
        this.calcTurndices();
        await this.sendMsg(Turfmaster.stateMachine.SELECTDICES, {});
      } else {
        await this.setNextMove();
      }
      return;
    }
    if (isSelectDicesState) {
      const dicePlayerId = this.game.turndiceplayerid;
      if (dicePlayerId === 0) {
        await playerView.receiveMsg(Turfmaster.stateMachine.SELECTDICES, {});
      } else {
        const dicePlayerView = this.playerViews[dicePlayerId] as Computer;
        if (dicePlayerView) {
          await dicePlayerView.receiveMsg(Turfmaster.stateMachine.SELECTDICES, {});
        }
      }
      return;
    }

    if (!isPickDiceState && !isAfterOppRoll) {
      for (let i = 1; i < this.playerViews.length; i++) {
        await (this.playerViews[i] as Computer).receiveMsg(curState, {});
      }
      return;
    }

    // Only trigger the current player; otherwise the controller rejects (e.g. bot acting on human's turn).
    // turnorder[turnpos] is horse index; use getPlayerId() for the player whose turn it is.
    // This prevents stuck games when returning from history mode or refreshing during a turn.
    const rawPlayerId = this.getPlayerId();
    const currentPlayerId = typeof rawPlayerId === 'number' ? rawPlayerId : 0;
    const horseid = this.getHorseId();
    const moves = currentPlayerId === 0 ? this.getMovesLogic(horseid, -1, false) : null;
    if (currentPlayerId === 0) {
      // Human's turn: resend SELECTMOVE with moves so the UI shows move options.
      await playerView.receiveMsg(Turfmaster.stateMachine.SELECTMOVE, {
        moves: moves ?? [],
        carddice: -1,
      });
    } else {
      // Bot's turn: resend SELECTCARDDICE so the bot picks and sends its move.
      // Bot will send SELECTMOVE even if no moves exist (see tmcomputer.ts selectCardDiceEvent).
      const computerView = this.playerViews[currentPlayerId] as Computer;
      if (computerView) {
        await computerView.receiveMsg(Turfmaster.stateMachine.SELECTCARDDICE, {});
      }
    }
  };
}

export default ClientControllerLocal;
